/**
 * Analytics Portal V1 — locked paid-tier preview (Phase 9, spec §2.3 + plan §12.4).
 *
 * The copy and both buttons come from the SERVER (`config.lock_copy`) so marketing text lives
 * in one place. Behind the overlay is a blurred skeleton only — no metric value, no entity
 * name, no protected payload, and nothing fabricated. The paywall itself is server-enforced:
 * a locked viewer's dashboard request would return `{locked:true}` with no data, and this
 * component means we never even ask.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoadingSkeleton } from "./LoadingSkeleton";

export const LockedOverlay = ({ lockCopy, tabs = [] }) => (
  <div className="relative" data-testid="analytics-locked">
    <div aria-hidden="true"><LoadingSkeleton cards={4} charts={2} blurred /></div>
    <div className="absolute inset-0 flex items-start justify-center px-4 pt-10 sm:pt-16">
      <div className="w-full max-w-xl rounded-2xl border border-border bg-card/95 p-6 shadow-xl backdrop-blur-sm">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent">
          <Lock className="h-5 w-5 text-primary" aria-hidden="true" />
        </span>
        <h2 className="mt-4 text-xl font-[680] tracking-tight text-foreground sm:text-2xl">
          {lockCopy?.heading || "Turn activity into actionable insight."}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground/80">
          {lockCopy?.body}
        </p>
        {tabs.length > 0 && (
          <ul className="mt-4 flex flex-wrap gap-2" data-testid="analytics-locked-tab-names">
            {tabs.map((tab) => (
              <li key={tab.key}
                  className="rounded-full border border-border bg-secondary/70 px-2.5 py-1 text-xs font-[560] text-muted-foreground">
                {tab.label}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          {(lockCopy?.buttons || []).map((button, index) => (
            <Button
              key={button.key}
              asChild
              variant={index === 0 ? "default" : "secondary"}
              data-testid={button.key === "upgrade_pro" ? "analytics-upgrade-pro"
                : button.key === "explore_capital" ? "analytics-explore-capital"
                  : `analytics-lock-${button.key}`}
            >
              <Link to={button.href || "/pricing"}>{button.label}</Link>
            </Button>
          ))}
        </div>
      </div>
    </div>
  </div>
);
